import {
  Achiever,
  Apprentice,
  Diligent,
  Disciplined,
  Explorer,
  Guardian,
  Mentor,
  Novice,
  Scholar,
  Strategist
} from 'assets/streak';
import {
  DATA_LIMIT,
  DEFAULT_ALIGNMENT_SCORE,
  EMBEDDED_EXPLAINER_VIDEOS,
  FOCUS_DURATION_THRESHOLD,
  FOCUS_END_DURATION_NUMBER_OF_DECIMAL_PLACES,
  FOCUS_SUMMARY,
  GOALS_LEVEL,
  GOALS_LEVEL_TARGET,
  LEADER_BOARD_DETAIL_METRIC_ID,
  LEVEL_KEYS,
  NUMBER_OF_GOALS_HEADERS,
  NUMBER_OF_VISIBLE_LEVELS,
  PERCENTAGE_MULTIPLIER,
  SECONDS_PER_MINUTE,
  TIME_UNIT_CONVERT_FACTOR
} from 'constants/general';
import { decrement, increment } from './helpers';
import {
  FocusModeDaySummaryItem,
  ProductivitySummaryOption,
  UserOnboardingStats
} from 'interfaces/user.interface';
import { t } from 'services/i18n.service';
import {
  ActivitySummary,
  DropDownSelectedValue,
  GraphData,
  HeatMapData,
  LevelRequirement,
  UserRank
} from 'interfaces';
import { ActivityType } from 'interfaces/common.interface';
import {
  formatAbbrMonthDayYear,
  formatDateToAbbrMonth,
  formatDateToYMD
} from './time.util';
import moment from 'moment';
import { DAYS_IN_WEEK, WEEKS_TO_SHOW } from 'constants/graph';
import { asArray } from 'utils/array.util';
import { clamp, roundToDecimals } from 'utils/number.util';

export const getLevelIconAndName = (level: number) => {
  const levelName = t(`level_name.${LEVEL_KEYS[level]}`);
  const pictures = [
    Novice,
    Apprentice,
    Explorer,
    Disciplined,
    Diligent,
    Strategist,
    Mentor,
    Scholar,
    Guardian,
    Achiever
  ];
  return { levelIcon: pictures[level], levelName };
};

export const computeVisibleHeaderRange = (level: number) => {
  const totalHeaders = decrement(NUMBER_OF_VISIBLE_LEVELS);
  return {
    start: level < totalHeaders ? 0 : increment(level - totalHeaders),
    end: level >= totalHeaders ? increment(level) : NUMBER_OF_VISIBLE_LEVELS
  };
};

export const buildRequirementsForTargetLevel = (
  level: number
): LevelRequirement[] => {
  const levelTargets = GOALS_LEVEL_TARGET[level];
  if (!levelTargets) return [];

  return Object.entries(levelTargets).map(([title, streak]) => {
    let url: string | undefined;
    if (title === t('stats_page.setup_a_focus_mode')) {
      url = EMBEDDED_EXPLAINER_VIDEOS.SET_UP_A_FOCUS_MODE;
    } else if (title === t('stats_page.setup_always_blocked_urls')) {
      url = EMBEDDED_EXPLAINER_VIDEOS.SETUP_ALWAYS_BLOCKED_URLS;
    } else if (title === t('stats_page.install_mobile_app')) {
      url = EMBEDDED_EXPLAINER_VIDEOS.INSTALL_MOBILE_APPLICATION;
    } else if (title === t('stats_page.edit_your_habits')) {
      url = EMBEDDED_EXPLAINER_VIDEOS.EDIT_YOUR_HABITS;
    }
    return { title, streak: streak as number, ...(url ? { url } : {}) };
  });
};

export const getCurrentStreakForRequirement = (
  title: string,
  stats?: UserOnboardingStats
): number => {
  if (!stats) return 0;
  const mapping: Record<string, number> = {
    [t('stats_page.morning_routine_streak')]:
      stats.morning_routine_completion_streak_days ?? 0,
    [t('stats_page.micro_break_streak')]:
      stats.break_routine_completion_streak_days ?? 0,
    [t('stats_page.evening_routine_streak')]:
      stats.evening_routine_completion_streak_days ?? 0,
    [t('stats_page.focus_mode_streak')]:
      stats.focus_mode_completion_streak_days ?? 0,
    [t('stats_page.chat_with_focus_bear')]: Number(
      stats.has_chatted_with_focus_bear ?? false
    ),
    [t('stats_page.setup_a_focus_mode')]: Number(
      stats.has_edited_focus_mode ?? false
    )
  };
  return mapping[title] ?? 0;
};

export const buildGoalHeadersAndRequirements = (
  onboardingStats?: UserOnboardingStats
) => {
  const level = onboardingStats?.level ?? GOALS_LEVEL.start;
  const headers = [
    t('stats_page.start'),
    ...Array.from({ length: NUMBER_OF_GOALS_HEADERS }).map((_, idx) =>
      t(`stats_page.level`, { level: increment(idx) })
    ),
    t('stats_page.end')
  ];

  let requirements: LevelRequirement[] = [];

  if (level) {
    requirements = [
      {
        title: t('stats_page.morning_routine_streak'),
        streak:
          onboardingStats?.morning_routine_completion_streak_days ??
          GOALS_LEVEL_TARGET[level][t('stats_page.morning_routine_streak')]
      },
      {
        title: t('stats_page.micro_break_streak'),
        streak:
          onboardingStats?.break_routine_completion_streak_days ??
          GOALS_LEVEL_TARGET[level][t('stats_page.micro_break_streak')]
      },
      {
        title: t('stats_page.evening_routine_streak'),
        streak:
          onboardingStats?.evening_routine_completion_streak_days ??
          GOALS_LEVEL_TARGET[level][t('stats_page.evening_routine_streak')]
      },
      {
        title: t('stats_page.focus_mode_streak'),
        streak:
          onboardingStats?.focus_mode_completion_streak_days ??
          GOALS_LEVEL_TARGET[level][t('stats_page.focus_mode_streak')]
      }
    ];

    level === GOALS_LEVEL.level_3 &&
      requirements.push(
        ...[
          {
            title: t('stats_page.chat_with_focus_bear'),
            streak: Number(
              onboardingStats?.has_chatted_with_focus_bear ?? false
            )
          },
          {
            title: t('stats_page.setup_a_focus_mode'),
            streak: Number(onboardingStats?.has_edited_focus_mode ?? false),
            url: EMBEDDED_EXPLAINER_VIDEOS.SET_UP_A_FOCUS_MODE
          }
        ]
      );
  } else {
    requirements = [
      {
        title: t('stats_page.setup_always_blocked_urls'),
        streak: Number(onboardingStats?.has_edited_always_blocked_urls),
        url: EMBEDDED_EXPLAINER_VIDEOS.SETUP_ALWAYS_BLOCKED_URLS
      },
      {
        title: t('stats_page.install_mobile_app'),
        streak: Number(onboardingStats?.has_installed_mobile_app),
        url: EMBEDDED_EXPLAINER_VIDEOS.INSTALL_MOBILE_APPLICATION
      },
      {
        title: t('stats_page.edit_your_habits'),
        streak: Number(onboardingStats?.has_edited_settings),
        url: EMBEDDED_EXPLAINER_VIDEOS.EDIT_YOUR_HABITS
      }
    ];
  }

  return { headers, requirements };
};

export interface ProcessedHeatMapData {
  weeks: WeekDay[][];
  maxCount: number;
  monthLabels: { label: string; weekIndex: number }[];
}

export interface WeekDay {
  date: moment.Moment;
  count: number;
}

export const getColorIntensity = (count: number, maxCount: number): string => {
  if (count === 0) return 'bg-gray-200 dark:bg-gray-600';
  const intensity = Math.min(count / Math.max(maxCount, 1), 1);
  if (intensity <= 0.25) return 'bg-green-200 dark:bg-green-900';
  if (intensity <= 0.5) return 'bg-green-400 dark:bg-green-700';
  if (intensity <= 0.75) return 'bg-green-500 dark:bg-green-600';
  return 'bg-green-600 dark:bg-green-500';
};

export const processHeatMapData = (
  data: HeatMapData[],
  userJoinDate?: string | Date | null
): ProcessedHeatMapData => {
  const dataMap = new Map<string, number>();
  let max = 0;

  if (!data || !Array.isArray(data)) {
    return { weeks: [], maxCount: 0, monthLabels: [] };
  }

  data.forEach((item) => {
    const dateKey = formatDateToYMD(item.date);
    const count = item.count || 0;
    dataMap.set(dateKey, count);
    if (count > max) max = count;
  });

  const endDate = moment().endOf('day');
  const defaultStartDate = moment()
    .subtract(WEEKS_TO_SHOW, 'weeks')
    .startOf('week');
  const joinDate = userJoinDate ? moment(userJoinDate).startOf('week') : null;
  const startDate =
    joinDate && joinDate.isAfter(defaultStartDate)
      ? joinDate
      : defaultStartDate;

  const weeksArray: WeekDay[][] = [];
  const months: { label: string; weekIndex: number }[] = [];
  let currentMonth = '';

  const currentDate = startDate.clone();
  let weekIndex = 0;

  while (currentDate.isSameOrBefore(endDate)) {
    const week: WeekDay[] = [];

    for (let day = 0; day < DAYS_IN_WEEK; day++) {
      if (currentDate.isSameOrBefore(endDate)) {
        const dateKey = formatDateToYMD(currentDate);
        const count = dataMap.get(dateKey) || 0;
        week.push({ date: currentDate.clone(), count });

        const monthLabel = formatDateToAbbrMonth(currentDate);
        if (monthLabel !== currentMonth && day === 0) {
          currentMonth = monthLabel;
          months.push({ label: monthLabel, weekIndex });
        }
      }

      currentDate.add(1, 'day');
    }

    // Only add week if it has days
    if (week.length > 0) {
      weeksArray.push(week);
      weekIndex++;
    }
  }
  return { weeks: weeksArray, maxCount: max, monthLabels: months };
};

export const calculateTotalCompletions = (
  heatMapData: HeatMapData[]
): number => {
  return asArray<HeatMapData>(heatMapData).reduce(
    (sum, item) => sum + item.count,
    0
  );
};

const SECONDS_PER_HOUR = 3600;

export const calculateTotalHoursSpent = (
  summaries: ActivitySummary[]
): number => {
  const totalSeconds = asArray<ActivitySummary>(summaries).reduce(
    (sum, item) => {
      const seconds = parseInt(item.summary ?? '0', 10);
      return sum + seconds;
    },
    0
  );
  return Math.round((totalSeconds / SECONDS_PER_HOUR) * 10) / 10;
};

export const calculateCurrentStreak = (heatMapData: HeatMapData[]): number => {
  const data = asArray<HeatMapData>(heatMapData);
  if (data.length === 0) return 0;

  const sortedData = [...data].sort(
    (firstItem, secondItem) =>
      new Date(secondItem.date).getTime() - new Date(firstItem.date).getTime()
  );

  let streak = 0;
  const yesterday = moment().subtract(1, 'day').startOf('day');

  for (const item of sortedData) {
    const itemDate = moment(item.date).startOf('day');
    const daysDiff = yesterday.diff(itemDate, 'days');

    if (daysDiff === streak && item.count > 0) {
      streak++;
    } else if (daysDiff > streak) {
      break;
    }
  }

  return streak;
};

const DAYS_FOR_PERCENTAGE = 30;
const PERCENTAGE_BASE = 100;

export const calculateLast30DaysPercentage = (
  heatMapData: HeatMapData[]
): number => {
  const data = asArray<HeatMapData>(heatMapData);
  if (data.length === 0) return 0;

  const today = moment().startOf('day');
  const thirtyDaysAgo = moment()
    .subtract(DAYS_FOR_PERCENTAGE, 'days')
    .startOf('day');

  const daysWithActivity = data.filter((item) => {
    const itemDate = moment(item.date).startOf('day');
    return (
      item.count > 0 &&
      itemDate.isSameOrAfter(thirtyDaysAgo) &&
      itemDate.isSameOrBefore(today)
    );
  }).length;

  return Math.round((daysWithActivity / DAYS_FOR_PERCENTAGE) * PERCENTAGE_BASE);
};

export const transformSummariesToHeatMapData = (
  summaries: ActivitySummary[]
): HeatMapData[] => {
  if (!summaries || !Array.isArray(summaries)) {
    return [];
  }
  return summaries.map((summary) => ({
    date: summary.date,
    count: parseInt(summary.count ?? '0', 10)
  }));
};

export const transformSummariesToGraphData = (
  summaries: ActivitySummary[]
): GraphData => {
  const sortedSummaries = [...asArray<ActivitySummary>(summaries)].sort(
    (firstSummary, secondSummary) =>
      new Date(firstSummary.date).getTime() -
      new Date(secondSummary.date).getTime()
  );
  return {
    labels: sortedSummaries.map((summary) =>
      formatAbbrMonthDayYear(summary.date)
    ),
    data: sortedSummaries.map((summary) => {
      const value = summary.summary;
      if (value === null || value === undefined) return 0;
      if (typeof value === 'number') return value;
      return parseFloat(value) || 0;
    })
  };
};

export const buildActivityOptions = (
  activities: ActivityType[]
): DropDownSelectedValue[] => {
  const options: DropDownSelectedValue[] = [];
  asArray<ActivityType>(activities).forEach((activity) => {
    options.push({
      label: activity.name,
      value: activity.id
    });
    activity.choices?.forEach((choice) => {
      options.push({
        label: `  ${choice.name}`,
        value: choice.id
      });
    });
  });
  return options;
};

export const buildQuestionOptions = (
  questions: Array<{ id: string; question: string }>
): DropDownSelectedValue[] => {
  return questions.map((question) => ({
    label: question.question,
    value: question.id
  }));
};

export const findActivityBySelectedValue = (
  activities: ActivityType[],
  selectedValue: string | number
): ActivityType | null => {
  if (!selectedValue) return null;
  return (
    activities.find((activity) => {
      if (activity.id === selectedValue) return true;
      return activity.choices?.some((choice) => choice.id === selectedValue);
    }) ?? null
  );
};

export const findOptionByValue = <T extends { value: string | number }>(
  options: T[],
  value: string | number
): T | undefined => {
  return options.find((option) => String(option.value) === String(value));
};

export type LeaderBoardDetailMetric = {
  id: string;
  titleKey: string;
  primaryValue: string;
  streak: string;
  daysCompleted: number;
  daysTotal: number;
};

const getLeaderBoardDaysPair = ({
  completed,
  total,
  fallbackCompleted,
  fallbackTotal
}: {
  completed?: number;
  total?: number;
  fallbackCompleted: number;
  fallbackTotal: number;
}) => ({
  daysCompleted: completed ?? fallbackCompleted,
  daysTotal: total ?? fallbackTotal
});

const buildMorningDetailMetric = (
  item: UserRank,
  fallbackCompleted: number,
  fallbackTotal: number
): LeaderBoardDetailMetric => ({
  id: LEADER_BOARD_DETAIL_METRIC_ID.MORNING,
  titleKey: 'leader_board.morning_streak',
  primaryValue: `${item.morning_percent_number_day_of_stats_completed}%`,
  streak: item.morning_routines_streak,
  ...getLeaderBoardDaysPair({
    completed: item.morning_number_days_completed,
    total: item.morning_num_days_of_stats,
    fallbackCompleted,
    fallbackTotal
  })
});

const buildBreakDetailMetric = (
  item: UserRank,
  fallbackCompleted: number,
  fallbackTotal: number
): LeaderBoardDetailMetric => ({
  id: LEADER_BOARD_DETAIL_METRIC_ID.BREAK,
  titleKey: 'leader_board.break_streak',
  primaryValue: item.micro_percent_number_day_of_stats_completed
    ? `${item.micro_percent_number_day_of_stats_completed}%`
    : String(item.micro_breaks_streak),
  streak: item.micro_breaks_streak,
  ...getLeaderBoardDaysPair({
    completed: item.micro_breaks_number_days_completed,
    total: item.micro_breaks_num_days_of_stats,
    fallbackCompleted,
    fallbackTotal
  })
});

const buildEveningDetailMetric = (
  item: UserRank,
  fallbackCompleted: number,
  fallbackTotal: number
): LeaderBoardDetailMetric => ({
  id: LEADER_BOARD_DETAIL_METRIC_ID.EVENING,
  titleKey: 'leader_board.evening_streak',
  primaryValue: `${item.evening_percent_number_day_of_stats_completed}%`,
  streak: item.evening_routines_streak,
  ...getLeaderBoardDaysPair({
    completed: item.evening_number_days_completed,
    total: item.evening_num_days_of_stats,
    fallbackCompleted,
    fallbackTotal
  })
});

const buildFocusDetailMetric = (
  item: UserRank,
  fallbackCompleted: number,
  fallbackTotal: number
): LeaderBoardDetailMetric => ({
  id: LEADER_BOARD_DETAIL_METRIC_ID.FOCUS,
  titleKey: 'leader_board.focus_mode',
  primaryValue: String(item.focus_modes_streak),
  streak: item.focus_modes_streak,
  ...getLeaderBoardDaysPair({
    completed: item.focus_modes_number_days_completed,
    total: item.focus_modes_num_days_of_stats,
    fallbackCompleted,
    fallbackTotal
  })
});

export const buildLeaderBoardRowDetailMetrics = (
  item: UserRank
): LeaderBoardDetailMetric[] => {
  const fallbackCompleted = item.number_days_completed;
  const fallbackTotal = item.num_days_of_stats;

  return [
    buildMorningDetailMetric(item, fallbackCompleted, fallbackTotal),
    buildBreakDetailMetric(item, fallbackCompleted, fallbackTotal),
    buildEveningDetailMetric(item, fallbackCompleted, fallbackTotal),
    buildFocusDetailMetric(item, fallbackCompleted, fallbackTotal)
  ];
};

export const calculateFocusedMinutes = (
  focusSessions: FocusModeDaySummaryItem[]
): number => {
  return asArray<FocusModeDaySummaryItem>(focusSessions).reduce(
    (total, session) => {
      const durationSeconds = Math.max(0, session.duration || 0);
      const alignmentScore =
        session.metadata?.focus_alignment_score ?? DEFAULT_ALIGNMENT_SCORE;
      const focusedSeconds =
        durationSeconds * (alignmentScore / PERCENTAGE_MULTIPLIER);
      return total + focusedSeconds / SECONDS_PER_MINUTE;
    },
    0
  );
};

const HOURS_PER_DAY =
  TIME_UNIT_CONVERT_FACTOR.ONE_DAY_AS_SECONDS /
  TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_SECONDS;
const HOURS_THRESHOLD_FOR_DAYS = 48;
const MINUTES_THRESHOLD_FOR_HOURS =
  FOCUS_DURATION_THRESHOLD.DEFAULT *
  TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_MINUTES;

export const FOCUS_TIME_UNIT = {
  MINUTES: 'minutes',
  HOURS: 'hours',
  DAYS: 'days'
} as const;

export const humanizeFocusedTime = (
  totalMinutes: number
): {
  value: number;
  unit: (typeof FOCUS_TIME_UNIT)[keyof typeof FOCUS_TIME_UNIT];
  remainingHours?: number;
} => {
  const safeMinutes = clamp(totalMinutes, 0, Number.POSITIVE_INFINITY);

  if (safeMinutes >= MINUTES_THRESHOLD_FOR_HOURS) {
    const hours = safeMinutes / TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_MINUTES;
    if (hours >= HOURS_THRESHOLD_FOR_DAYS) {
      const days = Math.floor(hours / HOURS_PER_DAY);
      const remainingHours = Math.round(hours % HOURS_PER_DAY);
      return { value: days, unit: FOCUS_TIME_UNIT.DAYS, remainingHours };
    }
    return {
      value: roundToDecimals(
        hours,
        FOCUS_END_DURATION_NUMBER_OF_DECIMAL_PLACES
      ),
      unit: FOCUS_TIME_UNIT.HOURS
    };
  }

  return {
    value: roundToDecimals(safeMinutes, 0),
    unit: FOCUS_TIME_UNIT.MINUTES
  };
};

export const calculateAverageTabMetrics = (
  focusSessions: FocusModeDaySummaryItem[]
): { avgCount: number; avgDuration: number } => {
  const sessionsWithTabData = asArray<FocusModeDaySummaryItem>(
    focusSessions
  ).filter(
    (session) =>
      session.metadata?.average_tab_count !== undefined ||
      session.metadata?.average_tab_duration !== undefined
  );

  if (sessionsWithTabData.length === 0) {
    return { avgCount: 0, avgDuration: 0 };
  }

  const totalTabCount = sessionsWithTabData.reduce(
    (sum, session) => sum + (session.metadata?.average_tab_count ?? 0),
    0
  );
  const totalTabDuration = sessionsWithTabData.reduce(
    (sum, session) => sum + (session.metadata?.average_tab_duration ?? 0),
    0
  );

  return {
    avgCount: totalTabCount / sessionsWithTabData.length,
    avgDuration: totalTabDuration / sessionsWithTabData.length
  };
};

const CHART_HOURS_THRESHOLD = 1000;

export const getGroupedFocusedMinutes = (
  focusSessions: FocusModeDaySummaryItem[],
  timeUnit: moment.unitOfTime.StartOf,
  limit: number
): { labels: string[]; data: number[]; useHours: boolean } => {
  const grouped: Record<string, number> = {};

  asArray<FocusModeDaySummaryItem>(focusSessions).forEach((session) => {
    const dateKey = moment(session.start_time).startOf(timeUnit).format();
    const durationSeconds = Math.max(0, session.duration || 0);
    const alignmentScore =
      session.metadata?.focus_alignment_score ?? DEFAULT_ALIGNMENT_SCORE;
    const focusedMinutes =
      (durationSeconds * (alignmentScore / PERCENTAGE_MULTIPLIER)) /
      SECONDS_PER_MINUTE;

    grouped[dateKey] = (grouped[dateKey] || 0) + focusedMinutes;
  });

  const sortedKeys = Object.keys(grouped)
    .sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
    .slice(-limit);

  const getFormatString = (unit: moment.unitOfTime.StartOf): string => {
    if (unit === FOCUS_SUMMARY.TIME_UNIT.DAY) return 'MMM DD';
    if (unit === FOCUS_SUMMARY.TIME_UNIT.MONTH) return 'MMM YYYY';
    return 'YYYY';
  };

  const formatString = getFormatString(timeUnit);
  const maxValue = Math.max(...sortedKeys.map((key) => grouped[key]), 0);
  const useHours = maxValue >= CHART_HOURS_THRESHOLD;

  return {
    labels: sortedKeys.map((key) => moment(key).format(formatString)),
    data: sortedKeys.map((key) =>
      useHours
        ? roundToDecimals(
            grouped[key] / TIME_UNIT_CONVERT_FACTOR.ONE_HOUR_AS_MINUTES,
            FOCUS_END_DURATION_NUMBER_OF_DECIMAL_PLACES
          )
        : roundToDecimals(grouped[key], 0)
    ),
    useHours
  };
};

export const getTimeUnit = (
  summary: ProductivitySummaryOption
): moment.unitOfTime.StartOf => {
  if (summary === FOCUS_SUMMARY.SUMMARY_OPTION.MONTHLY) {
    return FOCUS_SUMMARY.TIME_UNIT.MONTH;
  }
  if (summary === FOCUS_SUMMARY.SUMMARY_OPTION.YEARLY) {
    return FOCUS_SUMMARY.TIME_UNIT.YEAR;
  }
  return FOCUS_SUMMARY.TIME_UNIT.DAY;
};

export const calculateAverageFocusDurationPerDay = (
  focusSessions: FocusModeDaySummaryItem[]
): number => {
  const sessions = asArray<FocusModeDaySummaryItem>(focusSessions);
  if (!sessions.length) return 0;

  const dailyMinutes: Record<string, number> = {};

  sessions.forEach((session) => {
    const dateKey = moment(session.start_time).format('YYYY-MM-DD');
    const durationSeconds = Math.max(0, session.duration || 0);
    const alignmentScore =
      session.metadata?.focus_alignment_score ?? DEFAULT_ALIGNMENT_SCORE;
    const focusedMinutes =
      (durationSeconds * (alignmentScore / PERCENTAGE_MULTIPLIER)) /
      SECONDS_PER_MINUTE;

    dailyMinutes[dateKey] = (dailyMinutes[dateKey] || 0) + focusedMinutes;
  });

  const days = Object.keys(dailyMinutes).length;
  const totalMinutes = Object.values(dailyMinutes).reduce(
    (sum, val) => sum + val,
    0
  );

  return days > 0 ? Math.round(totalMinutes / days) : 0;
};

export const getMostFocusedDay = (
  focusSessions: FocusModeDaySummaryItem[]
): { date: string; minutes: number } | null => {
  const sessions = asArray<FocusModeDaySummaryItem>(focusSessions);
  if (!sessions.length) return null;

  const dailyMinutes: Record<string, number> = {};

  sessions.forEach((session) => {
    const dateKey = formatDateToYMD(session.start_time);
    const durationSeconds = Math.max(0, session.duration || 0);
    const alignmentScore =
      session.metadata?.focus_alignment_score ?? DEFAULT_ALIGNMENT_SCORE;
    const focusedMinutes =
      (durationSeconds * (alignmentScore / PERCENTAGE_MULTIPLIER)) /
      SECONDS_PER_MINUTE;

    dailyMinutes[dateKey] = (dailyMinutes[dateKey] || 0) + focusedMinutes;
  });

  let maxDate = '';
  let maxMinutes = 0;

  Object.entries(dailyMinutes).forEach(([date, minutes]) => {
    if (minutes > maxMinutes) {
      maxDate = date;
      maxMinutes = minutes;
    }
  });

  return maxDate ? { date: maxDate, minutes: Math.round(maxMinutes) } : null;
};

export const getDateRangeForSummary = (
  summary: ProductivitySummaryOption
): { from_time: string; to_time: string } => {
  const now = moment();
  const to_time = now.toISOString();

  let from_time: moment.Moment;

  if (summary === FOCUS_SUMMARY.SUMMARY_OPTION.DAILY) {
    from_time = moment()
      .subtract(DATA_LIMIT, 'days')
      .startOf(FOCUS_SUMMARY.TIME_UNIT.DAY);
  } else if (summary === FOCUS_SUMMARY.SUMMARY_OPTION.MONTHLY) {
    from_time = moment()
      .subtract(DATA_LIMIT, 'months')
      .startOf(FOCUS_SUMMARY.TIME_UNIT.MONTH);
  } else {
    from_time = moment()
      .subtract(DATA_LIMIT, 'years')
      .startOf(FOCUS_SUMMARY.TIME_UNIT.YEAR);
  }

  return {
    from_time: from_time.toISOString(),
    to_time
  };
};
